import React from 'react';
import { Prompt, Route, RouteComponentProps } from 'react-router';
import { Button, Grid, Header, Icon, Message, Transition } from 'semantic-ui-react';

import { clienteApi, Resultados } from 'src/api';
import { NombrePersonal as NombrePersonalDTO } from 'src/api/protocolo';
import { NombrePersonal } from 'src/componentes';
import { Contexto } from 'src/contexto';
import { compararNombresConApellidosPrimero } from 'src/utilidades';
import { ClaseDTO } from '../tipos';
import { Encabezado } from './Encabezado';
import { ListaDeAlumnos } from './ListaDeAlumnos';

type Props = RouteComponentProps<{ id: string }>;

class AsistenciaDelCurso extends React.Component<Props> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    public render() {
        return (
            <>
                <Route
                    exact
                    path='/asistencia/cursos/:id'
                    component={AsistenciaDelCursoClaseActual}
                />
            </>
        );
    }
}

type State = Readonly<{
    curso?: Readonly<Resultados<'detallesDelCursoDeId'>>;
    cargando: boolean;
    editado: boolean;
    guardando: boolean;
    ingresada: boolean;
    clase?: ClaseDTO;
    presentesExtra: readonly NombrePersonalDTO[];
}>;

class AsistenciaDelCursoClaseActual extends React.Component<Props, State> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    public state: State = {
        curso: undefined,
        cargando: true,
        editado: false,
        guardando: false,
        ingresada: false,
        presentesExtra: [],
        clase: undefined,
    };

    private readonly idCurso = Number(this.props.match.params.id);

    public componentDidMount() {
        window.addEventListener('beforeunload', this.handleUnload);

        this.setState({ cargando: true });
        if (!this.state.curso) {
            this.obtenerDetalleDelCurso()
                .then(() =>
                    this.obtenerAlumnosParaAsistencia().then(() =>
                        this.setState({ cargando: false }),
                    ),
                )
                .catch();
        } else {
            this.obtenerAlumnosParaAsistencia()
                .then(() => this.setState({ cargando: false }))
                .catch();
        }
    }

    public componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleUnload);
    }

    public render() {
        const { curso, cargando, editado, guardando, ingresada, clase } = this.state;

        if (cargando) return <Icon loading name='spinner' />;
        if (!curso) return null;

        if (curso.fechaDeLaProximaClase !== curso.fechaDeHoy) {
            return (
                <Header textAlign='center'>Hoy no hay clases, no puede pasar asistencia.</Header>
            );
        }

        if (!clase) {
            return (
                <Header textAlign='center'>
                    Solo puede pasar asistencia durante el horario de clases.
                </Header>
            );
        }

        if (clase.asistencias.length === 0) {
            return <Header textAlign='center'>El curso aún no tiene alumnos inscritos</Header>;
        }

        return (
            <>
                <Prompt
                    when={editado}
                    message='Parece que tiene cambios sin guardar, ¿desea salir de todos modos?'
                />

                <Encabezado curso={curso} numeroDeClase={clase.numeroDeClase} />

                <Grid centered>
                    <Grid.Column computer={10} mobile={16}>
                        <ListaDeAlumnos
                            asistencias={clase.asistencias}
                            borde={ingresada ? 'verde' : editado ? 'rojo' : undefined}
                            alPresionarFilaAlumno={this.invertirAsistenciaAlumno}
                        />

                        {this.state.presentesExtra.length > 0 && (
                            <Message
                                warning
                                onDismiss={() => this.setState({ presentesExtra: [] })}
                            >
                                <Message.Header>Alumnos que ya no están en el curso</Message.Header>

                                <Message.Content>
                                    Estos alumnos aparecieron inicialmente en la lista y se marcaron
                                    presente, pero ya no están en el curso. La asistencia de los
                                    otros alumnos se guardó pero estos fueron omitidos:
                                </Message.Content>

                                <Message.List>
                                    {this.state.presentesExtra.map((nombre, i) => (
                                        <Message.Item key={i}>
                                            <NombrePersonal nombre={nombre} patron='%P %M %N %S' />
                                        </Message.Item>
                                    ))}
                                </Message.List>
                            </Message>
                        )}
                    </Grid.Column>

                    <Grid.Row centered columns='3'>
                        <Grid.Column />

                        <Grid.Column textAlign='center'>
                            <Button
                                content='Ingresar asistencia'
                                primary
                                disabled={guardando || ingresada}
                                loading={guardando}
                                onClick={this.registrarAsistencia}
                            />
                        </Grid.Column>

                        <Grid.Column>
                            <Transition
                                visible={ingresada}
                                animation='fade left'
                                duration={500}
                                unmountOnHide
                            >
                                <Message positive compact>
                                    <Icon name='check' />
                                    Asistencia ingresada
                                </Message>
                            </Transition>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </>
        );
    }

    private readonly registrarAsistencia = () => {
        const { guardando, clase } = this.state;
        if (guardando || !clase) return;

        this.setState({ guardando: true, presentesExtra: [] });

        clienteApi
            .ejecutar('registrarAsistenciaDelCurso', {
                idCurso: this.state.curso!.id,
                timestampDeUltimaActualizacion: clase.timestampDeUltimaActualizacion,
                numeroDeClase: clase.numeroDeClase,
                fecha: undefined,
                asistencias: clase.asistencias.map(asis => ({
                    idAlumno: asis.idAlumno,
                    presente: asis.presente,
                    papel: asis.papel,
                })),
            })
            .then(async presentesExtra => {
                const alumnos = this.obtenerAlumnosParaAsistencia();

                this.setState({
                    ingresada: true,
                    presentesExtra: presentesExtra.map(
                        id => clase.asistencias.find(a => a.idAlumno === id)!.nombre,
                    ),
                });

                return alumnos;
            })
            .then(() => {
                this.setState({ guardando: false, editado: false });
            })
            .catch(error => {
                this.setState({ guardando: false });
                if (error?.tipo === 'ACTUALIZACION_CONCURRENTE') {
                    this.context.mostrarModalError(
                        'La lista que ve en pantalla quedó desactualizada respecto' +
                            ' a la que tiene ahora el servidor. Una posibilidad es' +
                            ' que alguien más haya hecho cambios a la lista desde' +
                            ' la última vez que la cargó. Lamentablemente NO FUE ' +
                            ' POSIBLE ingresar la asistencia, por favor recargue' +
                            ' la página y vuelva a ingresarla.',
                    );
                    this.obtenerAlumnosParaAsistencia()
                        .then(() => this.setState({ editado: false }))
                        .catch();
                } else if (error?.tipo === 'INVARIANTE_DOMINIO') {
                    this.context.mostrarModalError(error);
                    this.obtenerAlumnosParaAsistencia().catch();
                } else {
                    this.context.mostrarModalError(error);
                }
            });
    };

    private readonly invertirAsistenciaAlumno = (indice: number) => {
        this.setState(prevState => {
            const { clase } = prevState;
            if (!clase) return prevState;

            const asistenciasActualizadas = clase.asistencias.map((asis, i) =>
                indice === i
                    ? {
                          ...asis,
                          presente: asis.presente ? false : true,
                      }
                    : asis,
            );

            return {
                clase: {
                    ...clase,
                    asistencias: asistenciasActualizadas,
                },
                editado: true,
                ingresada: false,
            };
        });
    };

    private async obtenerDetalleDelCurso() {
        try {
            const curso = await clienteApi.ejecutar('detallesDelCursoDeId', this.idCurso);
            this.setState({ curso });
        } catch (error) {
            this.context.mostrarModalError(error);
            this.setState({ curso: undefined });
        }
    }

    private async obtenerAlumnosParaAsistencia() {
        try {
            const { curso } = this.state;

            if (!curso) return;

            const numeroDeClase = curso.numeroDeClase;
            if (!numeroDeClase) return;

            const alumnosParaAsistencia = await clienteApi.ejecutar(
                'alumnosParaAsistenciaDelCursoEnNumeroDeClase',
                {
                    idCurso: curso.id,
                },
            );

            if (!alumnosParaAsistencia) return;

            const asistenciasOrdenadasPorNombre = alumnosParaAsistencia.asistencias.sort((a, b) =>
                compararNombresConApellidosPrimero(a.nombre, b.nombre),
            );

            this.setState({
                clase: {
                    ...alumnosParaAsistencia,
                    asistencias: asistenciasOrdenadasPorNombre,
                },
            });
        } catch (error) {
            this.context.mostrarModalError(error);
            this.setState({
                clase: undefined,
            });
        }
    }

    private readonly handleUnload = (event: BeforeUnloadEvent) => {
        if (!this.state.editado) return;
        event.preventDefault();
        event.returnValue = '';
    };
}

export default AsistenciaDelCurso;



// WEBPACK FOOTER //
// ./src/modulos/Asistencia/paginas/AsistenciaDelCurso/index.tsx